import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Pagination({
  page = 1,
  limit = 10,
  total = 29,
  onPageChange,
  onLimitChange,
  fixed = true,
  constrainTo = null,
}) {
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const baseClass =
    "w-full flex items-center justify-between py-3 px-4 flex-nowrap";

  const containerClassBase = fixed
    ? `${baseClass} fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700`
    : `${baseClass} sticky bottom-0`;

  const [fixedStyle, setFixedStyle] = React.useState(null);

  React.useEffect(() => {
    if (!fixed || !constrainTo) return;

    function update() {
      const el = document.querySelector(constrainTo);
      if (!el) return setFixedStyle(null);
      const r = el.getBoundingClientRect();
      setFixedStyle({ left: `${r.left}px`, width: `${r.width}px` });
    }

    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [fixed, constrainTo]);

  return (
    <div
      className={`${containerClassBase} bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700`}
      style={
        fixed
          ? constrainTo && fixedStyle
            ? fixedStyle
            : { left: 0, right: 0 }
          : undefined
      }
    >
      {/* Left side text (truncates if space is tight) */}
      <div className="min-w-0 text-center sm:text-left">
        <p className="text-gray-600 text-xs truncate">
          Showing <span className="font-semibold">{start}</span> -
          <span className="font-semibold"> {end}</span> of total{" "}
          <span className="font-semibold">{total}</span>
        </p>
      </div>

      {/* Center buttons */}
      <div className="flex items-center gap-3 flex-shrink-0 justify-center">
        <button
          onClick={() => page > 1 && onPageChange(page - 1)}
          className={`flex items-center gap-1 px-3 py-[6px] rounded-lg text-xs 
            ${page === 1 ? "bg-gray-100 text-gray-400 dark:bg-slate-500/20" : "bg-blue-600  text-white "}`}
          disabled={page === 1}
        >
          <ChevronLeft size={16} /> Prev
        </button>

        <button
          onClick={() => end < total && onPageChange(page + 1)}
          className={`flex items-center gap-1 px-3 py-[6px] rounded-lg text-xs 
            ${end >= total ? "bg-gray-100 dark:bg-slate-500/20 text-gray-400" : "bg-blue-600 text-white"}`}
          disabled={end >= total}
        >
          Next <ChevronRight size={16} />
        </button>
      </div>

      {/* Rows per page */}
      <div className="flex items-center gap-2 text-gray-600 flex-shrink-0 justify-center sm:justify-end">
        <span className="text-xs">Rows per page:</span>
        <select
          value={limit}
          onChange={(e) => onLimitChange(Number(e.target.value))}
          className="border rounded-lg px-3 py-1 text-xs"
        >
          {[5, 10, 20, 30, 50].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
